import type {
  OverlayFigure,
  OverlayCreateFiguresCallbackParams,
} from "klinecharts";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const REPLAY_POSITION_LINE_NAME = "replayPositionLine";
export const REPLAY_START_LINE_NAME = "replayStartLine";

const DEFAULT_COLOR = "#0EC4B4";

function getLineColor(overlay: unknown): string {
  return (overlay as any)?.styles?.line?.color ?? DEFAULT_COLOR;
}

// ---------------------------------------------------------------------------
// ReplayPositionLine — solid line showing current replay position
// ---------------------------------------------------------------------------

const POSITION_LINE_COLOR_DIM = "rgba(14,196,180,0.45)";

function createReplayPositionLineTemplate() {
  return {
    name: REPLAY_POSITION_LINE_NAME,
    totalStep: 2,
    needDefaultPointFigure: false,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,
    lock: false,
    styles: {
      line: { color: POSITION_LINE_COLOR_DIM, style: "solid" as const, size: 1 },
    },

    createPointFigures(
      params: OverlayCreateFiguresCallbackParams<unknown>,
    ): OverlayFigure[] {
      const { coordinates, bounding, overlay } = params;
      if (coordinates.length < 1 || !bounding) return [];

      const coord = coordinates[0];
      if (!coord || coord.x == null) return [];

      const isHover = (overlay as any).state === "hover" || (overlay as any).state === "pressed";
      const color = isHover ? DEFAULT_COLOR : POSITION_LINE_COLOR_DIM;
      const size = isHover ? 2 : 1;
      const x = coord.x;
      const height = bounding.height ?? 0;

      return [
        {
          type: "line",
          attrs: {
            coordinates: [
              { x, y: 0 },
              { x, y: height },
            ],
          },
          styles: {
            color,
            style: "solid",
            size,
          },
        },
      ];
    },

    createXAxisFigures(): OverlayFigure[] {
      return [];
    },

    createYAxisFigures(): OverlayFigure[] {
      return [];
    },
  };
}

// ---------------------------------------------------------------------------
// ReplayStartLine — draggable vertical line to set replay start point
// ---------------------------------------------------------------------------

function createReplayStartLineTemplate() {
  return {
    name: REPLAY_START_LINE_NAME,
    totalStep: 2,
    needDefaultPointFigure: false,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,
    lock: false,
    styles: {
      line: { color: DEFAULT_COLOR, style: "dashed" as const, size: 1.5, dashedValue: [4, 4] },
      polygon: { color: "transparent" },
    },

    createPointFigures(
      params: OverlayCreateFiguresCallbackParams<unknown>,
    ): OverlayFigure[] {
      const { coordinates, bounding, overlay } = params;
      if (coordinates.length < 1 || !bounding) return [];

      const coord = coordinates[0];
      if (!coord || coord.x == null) return [];

      const baseColor = getLineColor(overlay);
      const isHover = (overlay as any).state === "hover" || (overlay as any).state === "pressed";
      const color = isHover ? baseColor + "dd" : baseColor;
      const x = coord.x;
      const height = bounding.height ?? 0;
      const showHandle = (overlay as any).extendData?.showHandle === true;

      const figures: OverlayFigure[] = [
        // Main vertical line
        {
          type: "line",
          attrs: {
            coordinates: [
              { x, y: 0 },
              { x, y: height },
            ],
          },
          styles: {
            color,
            style: isHover ? "solid" : "dashed",
            dashedValue: [4, 4],
            size: isHover ? 2.5 : 1.5,
          },
          ignoreEvent: false,
        },
      ];

      if (showHandle) {
        const midY = height / 2;
        const RADIUS = 14;
        const GRIP_W = 5;
        figures.push(
          {
            type: "circle",
            attrs: { x, y: midY, r: RADIUS },
            styles: { style: "fill", color: DEFAULT_COLOR },
            ignoreEvent: false,
          },
          // Three horizontal grip lines
          {
            type: "line",
            attrs: { coordinates: [{ x: x - GRIP_W, y: midY - 4 }, { x: x + GRIP_W, y: midY - 4 }] },
            styles: { color: "#000", size: 1.5 },
            ignoreEvent: true,
          },
          {
            type: "line",
            attrs: { coordinates: [{ x: x - GRIP_W, y: midY }, { x: x + GRIP_W, y: midY }] },
            styles: { color: "#000", size: 1.5 },
            ignoreEvent: true,
          },
          {
            type: "line",
            attrs: { coordinates: [{ x: x - GRIP_W, y: midY + 4 }, { x: x + GRIP_W, y: midY + 4 }] },
            styles: { color: "#000", size: 1.5 },
            ignoreEvent: true,
          },
        );
      }

      return figures;
    },
  };
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

let registered = false;

export async function registerReplayOverlays(): Promise<void> {
  if (registered) return;
  registered = true;

  try {
    const klinecharts = await import("klinecharts");
    klinecharts.registerOverlay(
      createReplayPositionLineTemplate() as Parameters<typeof klinecharts.registerOverlay>[0],
    );
    klinecharts.registerOverlay(
      createReplayStartLineTemplate() as Parameters<typeof klinecharts.registerOverlay>[0],
    );
  } catch {
    // Ignore SSR and duplicate registration issues.
  }
}
